import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, TableProps } from "antd";
import escapeRegExp from "lodash/escapeRegExp";
import { useContext, useEffect, useState } from "react";
import { HomeContext } from "../../containers/Home";
import { useLookup } from "../../hooks/useLookup";
import useSearch from "../../hooks/useSearch";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { getMyTasks } from "../../services/api-server/my_tasks";
import { getUsers } from "../../services/api-server/user";
import { TReportType } from "../../types/reporting.types";
import { GetAntIcon } from "../../utils/ant_icons";
import { formatDateTime } from "../../utils/date_formatter";
import { searchRecord } from "../../utils/searchRecord";
import { socket } from "../../utils/socket";
import { emailsEqual } from "../../utils/utils";
import ReportingFormDrawerV2 from "../Reporting/ReportingFormDrawerV2";
import SearchButton from "../SearchButton";

type TaskType =
	| "lesson_learned"
	| "incident_report"
	| "audit"
	| "moc"
	| "ofi"
	| "non_conformance"
	| "time_writing"
	| "chemical_management"
	| "project"
	| "operation"
	| "phase"
	| "task"
	| "deliverable";

type ModuleType =
	| "project-delivered"
	| "time-writing"
	| "lessons-learned"
	| "reporting"
	| "chemical-management";

interface MyTasksDataType {
	_id: string;
	operator: string;
	task_number: string;
	task_type: TaskType;
	name: string;
	description: string;
	assigned_on: string;
	assigned_by: string;
	assigned_to: string;
	deadline: string;
	module: ModuleType;
	status: "pending" | "completed";
	reference_id: string;
}

const getInitialTabKeyForTask = (task: {
	name?: string;
	taskType?: string;
}): string | undefined => {
	const normalizedName = (task.name ?? "").toLowerCase().replace(/[-_]/g, " ");
	if (
		task.taskType === "audit" &&
		normalizedName.includes("complete finding")
	) {
		return "findings";
	}
	if (normalizedName.includes("complete follow up action")) {
		return "follow_up_actions";
	}
	return undefined;
};

function MyTasksTable() {
	const [selectedTask, setSelectedTask] = useState<{
		taskId: string;
		taskType: TReportType | "lesson_learned";
		taskName: string;
	} | null>(null);
	// enable responsive window dimensions
	useWindowDimensions();

	const { userInfo }: any = useContext(HomeContext);
	const email: string | undefined = userInfo?.user?.email;
	const queryClient = useQueryClient();

	// Listen for real-time my_tasks refresh signals
	useEffect(() => {
		const userEmail = userInfo?.user?.email?.toLowerCase();
		if (!userEmail) return;

		const handleMyTasksUpdate = (payload: any) => {
			console.log(`[SOCKET RECEIVED] my_tasks for ${userEmail}:`, payload);
			queryClient.invalidateQueries({ queryKey: ["my_tasks"] });
		};

		socket.on("my_tasks", handleMyTasksUpdate);
		socket.on(`${userEmail}_my_tasks`, handleMyTasksUpdate);

		return () => {
			socket.off("my_tasks", handleMyTasksUpdate);
			socket.off(`${userEmail}_my_tasks`, handleMyTasksUpdate);
		};
	}, [userInfo?.user?.email, queryClient]);

	const { data, isLoading } = useQuery({
		queryKey: ["my_tasks"],
		queryFn: async () => {
			console.log(`[QUERY REFETCH] my_tasks fetching...`);
			const res = await getMyTasks({
				status: "pending",
				// Exact, case-insensitive match. A bare substring would also match
				// other people's addresses that contain this one.
				assigned_to: { $regex: `^${escapeRegExp(email)}$`, $options: "i" },
			});
			if (res && res.length > 0) {
				return res.map((item: any) => ({
					...item,
					task_title: `${item.name} - ${item.task_number}`,
				}));
			} else {
				return [];
			}
		},
		enabled: !!email,
		staleTime: 5 * 60 * 1000,
	});

	const { lookupData } = useLookup(["users"], data ?? [], [
		{
			fetch: (emails: string[]) => getUsers(emails) as Promise<any[] | null>,
			sourceKey: "assigned_by",
			targetKey: "email",
			outputKey: "assigned_by_name",
		},
	]);

	const [searchRecords, setSearchRecords] = useState<Record<string, string[]>>(
		{}
	);

	const columns: TableProps<MyTasksDataType>["columns"] = [
		{
			key: "name",
			title: (
				<SearchButton
					handleSearch={(value: any) => {
						setSearchRecords((prev: any) => {
							return {
								...prev,
								task_title: [value.value],
							};
						});
					}}
					headerName="Task"
					columnKey="name"
					key={"name"}
				/>
			),
			dataIndex: "name",
			sorter: (a: any, b: any) => {
				if (a?.task_title > b?.task_title) return 1;
				else if (b?.task_title > a?.task_title) return -1;
				else return 0;
			},
			render: (text: any, record: any) => {
				if (!record) return;

				let task_type: TaskType | undefined = undefined;
				let task_type_text: string | undefined = undefined;

				task_type = record?.task_type;

				if (
					[
						"project",
						"operation",
						"phase",
						"task",
						"deliverable",
						"incident_report",
						"audit",
						"moc",
						"ofi",
						"non_conformance",
						"lesson_learned",
					].includes(task_type as string)
				) {
					if (record?.task_type === "ofi") {
						task_type_text = "OFI";
					} else if (record?.task_type === "non_conformance") {
						task_type_text = "NCR";
					} else if (record?.task_type === "moc") {
						task_type_text = "MoC";
					} else if (record?.task_type === "lesson_learned") {
						task_type_text = "LL";
					} else {
						task_type_text = record?.task_type
							?.replace(/_/g, " ")
							.replace(/^./, (c: string) => c.toUpperCase());
					}
				}

				return (
					<div className="flex flex-col gap-2">
						<div
							className="cursor-pointer hover:opacity-70"
							onClick={() => {
								setSelectedTask({
									taskId: record.reference_id,
									taskType: record.task_type,
									taskName: record.name,
								});
							}}
						>
							<span className="text-(--default-btn-color)">
								{record?.task_number}
							</span>{" "}
							- {task_type_text && `[${task_type_text}]`} {text}
						</div>

						<div className="flex items-center gap-1 text-gray-500">
							<span>{GetAntIcon("apartment")}</span>
							<span>{record?.operator}</span>
						</div>
					</div>
				);
			},
		},
		{
			key: "description",
			title: (
				<SearchButton
					handleSearch={(value: any) => {
						setSearchRecords((prev: any) => {
							return {
								...prev,
								description: [value.value],
							};
						});
					}}
					headerName="Description"
					columnKey="description"
					key={"description"}
				/>
			),
			dataIndex: "description",
		},
		{
			key: "assigned_on",
			title: (
				<SearchButton
					handleSearch={(value: any) => {
						setSearchRecords((prev: any) => {
							return {
								...prev,
								assigned_by: [value.value],
							};
						});
					}}
					headerName="Assigned on"
					columnKey="assigned_on"
					key={"assigned_on"}
				/>
			),
			dataIndex: "assigned_on",
			sorter: (a, b) => {
				const dateA = a.assigned_on ? new Date(a.assigned_on) : null;
				const dateB = b.assigned_on ? new Date(b.assigned_on) : null;
				if (dateA && dateB) return dateA.getTime() - dateB.getTime();
				else if (dateA) return 1;
				else if (dateB) return -1;
				else return 0;
			},
			render: (text: any, record: any) => {
				if (!record) return;
				return (
					<p>
						{formatDateTime(text)} by{" "}
						<span
							className={`${
								localStorage.getItem("theme") === "dark"
									? "text-white/45"
									: "text-black/45"
							}`}
						>
							{
								lookupData?.assigned_by?.find((item: any) =>
									emailsEqual(item?.email, record?.assigned_by)
								)?.name
							}{" "}
							{record?.assigned_by
								? `(${record?.assigned_by})`
								: "Anonymous User"}
						</span>
					</p>
				);
			},
		},
		{
			key: "deadline",
			title: "Due date",
			dataIndex: "deadline",
			width: 150,
			sorter: (a, b) => {
				const dateA = a.deadline ? new Date(a.deadline) : null;
				const dateB = b.deadline ? new Date(b.deadline) : null;
				if (dateA && dateB) return dateA.getTime() - dateB.getTime();
				else if (dateA) return 1;
				else if (dateB) return -1;
				else return 0;
			},
			render: (text: any) => {
				if (!text) return;
				// convert to 10 June 2025 format
				const formattedDate = new Date(text).toLocaleDateString("en-GB", {
					day: "numeric",
					month: "short",
					year: "numeric",
				});
				return <span>{formattedDate}</span>;
			},
		},
	];

	const dataTable = useSearch(
		data,
		searchRecords,
		searchRecord({ searchRecords }) as any
	)?.sort((a, b) => {
		return (
			new Date(b.assigned_on).getTime() - new Date(a.assigned_on).getTime()
		);
	});

	const handleDrawerClose = () => {
		setSelectedTask(null);
	};

	return (
		<div className="flex-1 overflow-auto h-full">
			<div className="flex-1 p-5 ">
				<Table
					rootClassName="custom-table"
					dataSource={dataTable}
					columns={columns}
					pagination={{
						defaultPageSize: 20,
						pageSizeOptions: [20, 50, 100],
						showSizeChanger: true,
						position: ["bottomCenter"],
					}}
					bordered
					loading={isLoading}
					showSorterTooltip={false}
					scroll={{
						y: window.innerHeight - 350,
						x: 0,
					}}
				/>
				<ReportingFormDrawerV2
					isOpen={!!selectedTask}
					reportType={selectedTask?.taskType as TReportType | "lesson_learned"}
					onClose={handleDrawerClose}
					activeId={selectedTask?.taskId}
					initialTabKey={
						selectedTask
							? getInitialTabKeyForTask({
									name: selectedTask.taskName,
									taskType: selectedTask.taskType,
								})
							: undefined
					}
				/>
			</div>
		</div>
	);
}

export default MyTasksTable;
